import { DobButton, DobDateRangeExtended, DobLabel } from '@dedalusdiit/dob-components';
import { useTranslation } from 'react-i18next';
import { convertToValidDate, type DateValue } from '@/Helpers/DateHelpers';
import DateInputWrapper from '../../DateInputWrapper/DateInputWrapper';

export interface StudyDateFilterProps {
    filterIsActive: boolean;
    label: string;
    selectedFromDate?: Date;
    selectedPreset?: string;
    selectedUntilDate?: Date;
    onChange: (update?: { from?: Date; until?: Date; preset?: string }) => void;
    setStudyDateFilterActive: (isActive: boolean) => void;
}

const Presets = {
    Today: 'today',
    Yesterday: 'yesterday',
    LastTwoDays: '2d',
    LastSevenDays: '7d',
    LastThirtyDays: '30d',
} as const;
type Preset = keyof typeof Presets;

interface DateRangeChangeParams {
    from: DateValue;
    until: DateValue;
    preset: string | null;
}

const StudyDateFilter = (props: StudyDateFilterProps): React.ReactElement => {
    const { filterIsActive, label, selectedFromDate, selectedPreset, selectedUntilDate, onChange, setStudyDateFilterActive } = props;
    const { t } = useTranslation();

    const allPresetKeys = Object.keys(Presets) as Preset[];
    const presets = allPresetKeys.map((presetKey) => ({ label: t(presetKey), value: Presets[presetKey] }));

    const onChangeHandler = ({ from, until, preset }: DateRangeChangeParams): void => {
        const noneProvided = !(from ?? until ?? preset);
        const fromDate = convertToValidDate(from);
        const untilDate = convertToValidDate(until);
        const update = noneProvided
            ? undefined
            : {
                    from: fromDate,
                    until: untilDate,
                    preset: preset ?? undefined,
                };
        onChange(update);
        const presetChanged = preset && preset !== selectedPreset;
        const untilDateChanged = untilDate && untilDate.getTime() !== selectedUntilDate?.getTime();
        const fromAndUntilAreDifferent = fromDate && untilDate && fromDate.getTime() !== untilDate.getTime();

        if (presetChanged || (untilDateChanged && fromAndUntilAreDifferent)) {
            setStudyDateFilterActive(false);
        }
    };
    const onApplyHandler = ({ from, until, preset }: DateRangeChangeParams): void => {
        const fromDate = convertToValidDate(from);
        const untilDate = convertToValidDate(until);
        const finalPreset = preset ?? undefined;

        const noneProvided = !(fromDate ?? finalPreset);
        const update = noneProvided
            ? undefined
            : {
                    from: fromDate,
                    until: untilDate,
                    preset: finalPreset,
                };

        onChange(update);
        setStudyDateFilterActive(false);
    };

    const onResetHandler = (): void => {
        onChange(undefined);
    };

    if (filterIsActive) {
        return (
            <div>
                <div style={{ display: 'flex' }}>
                    <DobButton icon="arrow-left-short" size="small" color="silent" onClick={() => setStudyDateFilterActive(false)} />
                    <DobLabel text={label} />
                </div>
                <br />
                <DobDateRangeExtended
                    selectedPreset={selectedPreset}
                    selectedFromDate={selectedFromDate}
                    selectedUntilDate={selectedUntilDate}
                    showApplyButton={true}
                    onChange={onChangeHandler}
                    onApply={onApplyHandler}
                    presets={presets}
                />
            </div>
        );
    }

    return (
        <DateInputWrapper
            label={label}
            onClick={() => setStudyDateFilterActive(true)}
            from={selectedFromDate}
            until={selectedUntilDate}
            preset={selectedPreset}
            onReset={onResetHandler}
        />
    );
};

export default StudyDateFilter;
